import type { Image } from '@app/bindx-schema'
import type { HasOneRef } from '@contember/bindx'
import { Field, HasOne, useHasOne, withCollector } from '@contember/bindx-react'
import { Button, FormContainer, InputField, Progress } from '@contember/bindx-ui'
import { ImagePlusIcon, Trash2Icon } from 'lucide-react'
import { type DragEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import { ALLOWED_IMAGE_ACCEPT, ALLOWED_IMAGE_LABELS, isRenderableImage, readImageDimensions } from './upload'
import { type UploadedFile, useUpload } from './use-upload'

export const IMAGE_ALT_REQUIRED_MESSAGE = 'Alternative text is required.'

/**
 * One box for both states, so the form does not reflow when a picture lands in
 * an empty slot — and so every image slot in the admin is the same shape
 * whatever it holds. A slot whose picture has proportions of its own overrides
 * it through `boxClassName` (the OG image is the one that does).
 */
export const IMAGE_BOX = 'size-36'

/** 1200×630 is shared in a wide preview, so a wide tile says more than a square. */
export const IMAGE_BOX_WIDE = 'h-36 w-68'

/**
 * Rides in over the picture on hover. Outlined in white because it sits on a
 * scrim over an unknown photograph, where a filled button can vanish into it.
 */
const OVERLAY_BUTTON =
	'pointer-events-auto w-24 cursor-pointer rounded-md border border-white/80 px-2 py-1 text-xs font-medium text-white transition-colors hover:border-white hover:bg-white hover:text-blue-primary disabled:opacity-50'

type PendingImage = Pick<Image, 'url' | 'width' | 'height' | 'fileName' | 'fileSize' | 'mimeType'>

export interface ImageFieldProps {
	field: HasOneRef<Image>
	label: string
	description?: ReactNode
	/** Marks the label; the relation itself is enforced server-side where the model says notNull. */
	required?: boolean
	/** S3 prefix; defaults to assets. Partner logos use assets/partners. */
	uploadPrefix?: string
	/**
	 * Size of the tile, for a slot whose picture has a shape of its own. Filled
	 * and empty share it, so the form does not reflow on upload.
	 */
	boxClassName?: string
}

/**
 * `manyHasOne → Image` slot: upload (S3, `assets/`), alt text, replace and
 * remove. The `Image` row is created in an effect after the upload resolves,
 * never during render (NPI gotcha under React 19 concurrent rendering).
 *
 * Shaped after the Svět neziskovek slot: a fixed tile that looks the same
 * empty and filled — dashed placeholder before the upload, the picture itself
 * after it, with Replace on hover and the row actions in the corner. No
 * library picker yet (F2 nice-to-have) and no focal point (see below).
 */
export const ImageField = withCollector(
	function ImageFieldRuntime({ field, label, description, required, uploadPrefix, boxClassName = IMAGE_BOX }: ImageFieldProps) {
		const image = useHasOne(field)
		const [pending, setPending] = useState<PendingImage | null>(null)
		const [isDropTarget, setIsDropTarget] = useState(false)

		const onUploaded = useCallback(async (uploaded: UploadedFile) => {
			const size = await readImageDimensions(uploaded.file)
			setPending({
				url: uploaded.url,
				width: size?.width ?? null,
				height: size?.height ?? null,
				fileName: uploaded.fileName,
				fileSize: uploaded.fileSize,
				mimeType: uploaded.mimeType,
			})
		}, [])

		const accept = useCallback(
			(file: File) => isRenderableImage(file) ? null : `We cannot display the format of “${file.name}”. Please use: ${ALLOWED_IMAGE_LABELS}.`,
			[],
		)
		const upload = useUpload({ accept, onUploaded, prefix: uploadPrefix })

		// Store write in an effect: replace = drop the old relation (the row stays,
		// other usages keep it) and create a fresh one seeded with every scalar —
		// bindx applies no schema defaults.
		useEffect(() => {
			if (!pending) return
			if (image.$isConnected) image.$disconnect()
			image.$create({
				...pending,
				alt: null,
				focalX: null,
				focalY: null,
				sourcePath: null,
			})
			setPending(null)
		}, [pending, image])

		const connected = image.$isConnected
		const altHasError = connected ? image.alt.hasError : false

		// Alt text is recommended, not required (client decision): clear any error
		// an earlier build added, so it cannot keep blocking `persistAll`.
		useEffect(() => {
			if (connected && altHasError) image.alt.clearErrors()
		}, [connected, altHasError, image])

		const isUploading = upload.state.status === 'uploading'
		const url = connected ? image.url.value : null

		const onDrop = useCallback((event: DragEvent<HTMLElement>) => {
			setIsDropTarget(false)
			upload.onDrop(event)
		}, [upload])

		// `dragleave` fires on every child the pointer crosses, so the flag is kept
		// only while the pointer is still inside the drop zone itself.
		const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
			if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
			setIsDropTarget(false)
		}, [])

		return (
			<div className="space-y-3">
				<FormContainer description={description} label={label} required={required}>
					<div
						className="space-y-2"
						onDragEnter={() => setIsDropTarget(true)}
						onDragLeave={onDragLeave}
						onDragOver={upload.onDragOver}
						onDrop={onDrop}
					>
						<input
							accept={ALLOWED_IMAGE_ACCEPT}
							className="sr-only"
							disabled={isUploading}
							onChange={upload.onInputChange}
							ref={upload.inputRef}
							type="file"
						/>
						{url
							? (
								/* Filled, the picture is the control: Replace rides in on hover and
								   the corner icon removes the picture. The dashed box and its
								   instructions only earn their space while the slot is still empty.

								   Focal point is parked (client, 08/2026): the crosshair cursor and
								   the marker sat on top of the picture and got in the way of Replace.
								   `focalX` / `focalY` stay in the schema and in the query. */
								<div className={`group relative overflow-hidden rounded-md border border-light-gray bg-bg-gray ${boxClassName}`}>
									<img alt="" className="size-full object-contain" src={url} />
									<span className="pointer-events-none absolute inset-0 hidden place-items-center bg-blue-primary/55 group-hover:grid">
										<button className={OVERLAY_BUTTON} disabled={isUploading} onClick={upload.openPicker} type="button">
											{isUploading ? 'Uploading…' : 'Replace'}
										</button>
									</span>
									<span className="absolute right-1.5 top-1.5 z-20 flex gap-1">
										<button
											aria-label="Remove"
											className="grid h-7 w-7 place-items-center rounded-md bg-white/90 text-gray shadow-sm transition-colors hover:text-danger"
											onClick={() => image.$disconnect()}
											type="button"
										>
											<Trash2Icon className="h-4 w-4" />
										</button>
									</span>
								</div>
							)
							: (
								/* Empty, the slot is a dashed box the size of the picture that will
								   fill it, so the form does not change shape on upload. */
								<div
									className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-3 text-center transition-colors ${boxClassName} ${
										isDropTarget ? 'border-orange bg-orange/10' : 'border-gray/45 bg-white'
									}`}
								>
									<Button
										className="w-full cursor-pointer border-light-gray text-xs transition-colors hover:border-orange hover:bg-orange hover:text-white"
										disabled={isUploading}
										onClick={upload.openPicker}
										size="sm"
										type="button"
										variant="secondary"
									>
										<ImagePlusIcon className="mr-1.5 h-4 w-4" />
										{isUploading ? 'Uploading…' : 'Upload'}
									</Button>
									{isUploading
										? <Button className="h-6" onClick={upload.cancel} size="sm" type="button" variant="ghost">Cancel</Button>
										: <p className="text-[11px] text-gray">or drop a file here</p>}
								</div>
							)}
						{isUploading && upload.state.status === 'uploading'
							? (
								<div className="space-y-1">
									<Progress value={upload.state.progress} />
									<p className="text-xs text-gray">{upload.state.fileName} · {upload.state.progress} %</p>
								</div>
							)
							: null}
						{upload.state.status === 'error' ? <p className="text-sm text-danger">{upload.state.error}</p> : null}
						{
							/* Below the tile, not in its corner: the popover it used to open was
						    clipped by the tile's own `overflow-hidden` and half of it sat off the
						    picture. It only appears once there is a picture to describe — before
						    the upload there is no `Image` row to write to either. */
						}
						{url
							? (
								<div className="max-w-md">
									<InputField
										field={image.alt}
										inputProps={{ placeholder: 'e.g. Students at a lecture in Prague' }}
										label="Alternative text"
									/>
								</div>
							)
							: null}
					</div>
				</FormContainer>
			</div>
		)
	},
	({ field }: ImageFieldProps) => (
		<HasOne field={field}>
			{image => (
				<>
					<Field field={image.url} format={() => null} />
					<Field field={image.alt} format={() => null} />
					<Field field={image.width} format={() => null} />
					<Field field={image.height} format={() => null} />
					<Field field={image.fileName} format={() => null} />
					<Field field={image.fileSize} format={() => null} />
					<Field field={image.mimeType} format={() => null} />
					<Field field={image.focalX} format={() => null} />
					<Field field={image.focalY} format={() => null} />
					<Field field={image.sourcePath} format={() => null} />
				</>
			)}
		</HasOne>
	),
)
